import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useSeo } from "@/hooks/useSeo";

const FAQS = [
  {
    q: "How much does exterior cleaning cost?",
    a: "The cost depends on the size of the property, the surface being cleaned, the level of contamination, access and the cleaning method required. StoneCrest provides a free property assessment and a clear fixed-price quote before work begins.",
  },
  {
    q: "Do you provide free quotes?",
    a: "Yes. We provide free, no-obligation quotations for exterior cleaning work across Lancashire. We assess the property and explain the recommended cleaning method before confirming the price.",
  },
  {
    q: "Do you pressure wash roofs?",
    a: "Not every roof is suitable for pressure washing. We assess the roof type, condition and level of organic growth before choosing the most appropriate cleaning method. Depending on the roof, this may involve moss removal, controlled cleaning and a professional biocide treatment.",
  },
  {
    q: "Can you remove moss from my roof?",
    a: "Yes. Our roof cleaning service can include the removal of moss and other organic growth. Where appropriate, we can also apply a professional treatment to target remaining algae, lichen and microscopic organic growth.",
  },
  {
    q: "How long does roof cleaning last?",
    a: "Results vary depending on the roof, nearby trees, shade, moisture levels and local conditions. Professional treatment can help slow the return of organic growth, but exterior surfaces naturally weather over time.",
  },
  {
    q: "Is softwashing safe for render?",
    a: "Softwashing is designed for exterior surfaces where high pressure may be unsuitable. The treatment, dilution and application method should always be selected according to the type and condition of the render. We assess the surface before deciding on the most appropriate approach.",
  },
  {
    q: "What is the difference between softwashing and pressure washing?",
    a: "Pressure washing relies mainly on controlled water pressure to remove surface dirt and contamination. Softwashing uses specialist cleaning treatments at lower pressure to target organic growth such as algae, moss and lichen. The correct method depends on the surface being cleaned.",
  },
  {
    q: "Can you clean K-Rend and other rendered surfaces?",
    a: "Many rendered surfaces, including suitable K-Rend finishes, can be professionally cleaned using an appropriate low-pressure or softwashing method. We assess the condition of the render before selecting the most suitable cleaning process.",
  },
  {
    q: "Do you clean driveways and patios?",
    a: "Yes. We provide professional driveway and patio cleaning for suitable exterior surfaces including block paving, concrete and various types of stone. The exact cleaning method depends on the surface and its condition.",
  },
  {
    q: "Do you re-sand block paving after cleaning?",
    a: "Where jointing sand has been removed during the cleaning process, re-sanding may be recommended once the block paving is sufficiently dry. This can be discussed as part of your quotation.",
  },
  {
    q: "Can you remove black spots from patios?",
    a: "Some black spotting and organic staining can be significantly improved using specialist cleaning treatments. Results depend on the surface type, age of the staining and condition of the paving, so we assess the area before recommending a treatment.",
  },
  {
    q: "How often should gutters be cleared?",
    a: "How frequently gutters need clearing depends on the property and its surroundings. Homes close to trees or properties with heavy roof moss may need more frequent maintenance than homes in more open locations.",
  },
  {
    q: "Do you clean fascias and soffits?",
    a: "Yes. We clean fascias, soffits and other suitable exterior roofline surfaces to remove dirt, algae, traffic film and general weather staining.",
  },
  {
    q: "Do you work on commercial properties?",
    a: "Yes. StoneCrest provides exterior cleaning for residential and commercial properties, including managed lettings, offices, shops and other business premises.",
  },
  {
    q: "Which areas of Lancashire do you cover?",
    a: "We work across Preston, Chorley, Leyland, Blackburn, Blackpool, Lancaster and many surrounding Lancashire towns and villages. If your location is not listed on our website, contact us and we will confirm whether we cover your area.",
  },
  {
    q: "Are you fully insured?",
    a: "Yes. StoneCrest provides fully insured exterior cleaning services for residential and commercial properties.",
  },
  {
    q: "What happens before you start the work?",
    a: "Every job begins with an assessment of the property and the surfaces that need cleaning. We explain the recommended cleaning method, provide a clear quotation and confirm the scope of work before the job begins.",
  },
];

export default function FAQ() {
  useSeo(
    "Exterior Cleaning FAQs | Roof, Render & Driveway Cleaning | StoneCrest",
    "Answers to common questions about roof cleaning, render softwashing, driveway and patio cleaning, gutter clearing, pricing and exterior cleaning across Lancashire.",
    "https://stonecrestexteriors.co.uk/faq"
  );

  return (
    <div data-testid="faq-page">
      <PageHeader
        overline="Exterior Cleaning FAQ"
        lines={["Your exterior cleaning", "questions, answered."]}
        subtitle="Straightforward answers about roof cleaning, softwashing, pressure cleaning, gutters, pricing, safety and what to expect when you book StoneCrest."
      />

      {/* Introduction */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-10">
        <div className="max-w-3xl space-y-5 text-lg text-[var(--muted)] leading-relaxed">
          <p>
            Choosing the right exterior cleaning method can make a big
            difference to both the appearance of your property and the
            protection of its surfaces. Different materials require different
            approaches, which is why we assess every property before
            recommending a cleaning process.
          </p>

          <p>
            Below you will find answers to common questions about{" "}
            <Link to="/services/roof-cleaning" className="text-[var(--oxblood)] underline underline-offset-2 hover:text-[var(--oxblood-hover)] transition-colors">roof cleaning</Link>,{" "}
            <Link to="/services/render-softwashing" className="text-[var(--oxblood)] underline underline-offset-2 hover:text-[var(--oxblood-hover)] transition-colors">render softwashing</Link>,{" "}
            <Link to="/services/driveway-patio-cleaning" className="text-[var(--oxblood)] underline underline-offset-2 hover:text-[var(--oxblood-hover)] transition-colors">driveway and patio cleaning</Link>,{" "}
            <Link to="/services/gutter-clearing" className="text-[var(--oxblood)] underline underline-offset-2 hover:text-[var(--oxblood-hover)] transition-colors">gutter clearing</Link>,
            pricing, safety and the areas we cover across Lancashire. If your
            question is not listed, you can contact StoneCrest directly and we
            will be happy to help.
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-16 md:pb-20">
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((faq, index) => (
              <AccordionItem
                key={faq.q}
                value={`item-${index}`}
                className="border-b border-[var(--border)]"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="text-left font-serif text-xl md:text-2xl py-6 hover:no-underline hover:text-[var(--oxblood)]">
                  {faq.q}
                </AccordionTrigger>

                <AccordionContent className="text-[var(--muted)] text-base leading-relaxed pb-6">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Service Links */}
      <section className="bg-[var(--bone)]">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-14 md:py-16">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">
              Explore Our Services
            </p>

            <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight mb-8">
              Need more information about a specific service?
            </h2>

            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
              <Link
                to="/services/roof-cleaning"
                className="group flex items-center justify-between border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
              >
                <span>Roof Cleaning</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>

              <Link
                to="/services/render-softwashing"
                className="group flex items-center justify-between border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
              >
                <span>Render Softwashing</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>

              <Link
                to="/services/driveway-patio-cleaning"
                className="group flex items-center justify-between border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
              >
                <span>Driveway & Patio Cleaning</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>

              <Link
                to="/services/gutter-clearing"
                className="group flex items-center justify-between border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
              >
                <span>Gutter Clearing</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>

              <Link
                to="/services/fascia-soffit-cleaning"
                className="group flex items-center justify-between border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
              >
                <span>Fascia & Soffit Cleaning</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>

              <Link
                to="/areas/lancashire"
                className="group flex items-center justify-between border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
              >
                <span>Areas We Cover</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}